import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Package, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { HOME } from "../constants/testIds";
import ConnectionBadge from "../components/storefront/ConnectionBadge";
import ProductGrid from "../components/storefront/ProductGrid";
import { fetchShopifyHealth, fetchShopifyProducts } from "../lib/api";

export default function ConnectionPage() {
  const health = useQuery({
    queryKey: ["shopify", "health"],
    queryFn: fetchShopifyHealth,
    refetchOnWindowFocus: false,
  });

  const products = useQuery({
    queryKey: ["shopify", "products", { first: 20 }],
    queryFn: () => fetchShopifyProducts({ first: 20 }),
    refetchOnWindowFocus: false,
  });

  const refreshAll = () => {
    health.refetch();
    products.refetch();
  };
  const isRefreshing = health.isFetching || products.isFetching;

  return (
    <div className="mx-auto max-w-6xl px-6 py-12 md:py-16">
      <header className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <span>Loadout</span>
            <span aria-hidden>·</span>
            <span>Diagnostics · Connection</span>
          </div>
          <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">
            Headless Shopify storefront
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground md:text-base">
            Live view of the backend proxy → Shopify Storefront API. Handy when
            you want to confirm the pipe is up without loading the whole site.
          </p>
        </div>
        <Button
          data-testid={HOME.refreshButton}
          variant="outline"
          onClick={refreshAll}
          disabled={isRefreshing}
          className="self-start md:self-auto"
        >
          {isRefreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </header>

      <section className="mb-12">
        <ConnectionBadge
          isLoading={health.isLoading}
          isError={health.isError}
          data={health.data}
          error={health.error}
        />
      </section>

      <section>
        <div className="mb-6 flex items-end justify-between border-b border-border pb-3">
          <div className="flex items-center gap-3">
            <Package className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-lg font-medium">Live products</h2>
          </div>
          <span data-testid={HOME.productsCount} className="text-xs uppercase tracking-widest text-muted-foreground">
            {products.isLoading ? "Loading…" : products.isError ? "—" : `${products.data?.count ?? 0} shown`}
          </span>
        </div>

        <ProductGrid
          isLoading={products.isLoading}
          isError={products.isError}
          data={products.data}
          error={products.error}
        />
      </section>
    </div>
  );
}
